// /review: the Friday look back. What changed and why, planner bugs and
// other requests, issues, ratings, other work, and devices as an independent
// check (logged at final assembly against counted on S16).
import type { ReviewView } from "../lib/types";
import { TaskChip } from "../design";
import { useApi } from "../api/live";
import { paths } from "../api/paths";
import { shortDay, stamp, weekLabel } from "../lib/days";
import { useTasks } from "../lib/useRegister";
import { Page } from "../shell/Page";
import { ErrorNote, Loading } from "../shell/ui";

const CATEGORY: Record<string, string> = { absence: "Absence", missing_part: "Missing part", new_starter: "New starter", planner_bug: "Planner bug", other: "Other" };

export function ReviewPage() {
  const review = useApi<ReviewView>(paths.review(), "all");
  const { byCode } = useTasks();
  const r = review.data;
  if (!r) return <Page title="Review">{review.error ? <ErrorNote error={review.error} retry={review.reload} /> : <Loading />}</Page>;
  const bugs = r.requests.filter((q) => q.category === "planner_bug");
  return (
    <Page title="Review">
      <div className="page__head"><h1>{weekLabel(r.weekStart)}</h1></div>
      <div className="stack">
        <div className="tiles">
          <div className="tile"><div className="tile__n">{r.devicesLogged}</div><div className="tile__k">devices logged at final assembly</div></div>
          <div className="tile"><div className="tile__n">{r.devicesCounted ?? "–"}</div><div className="tile__k">devices counted on S16{r.devicesCounted != null && r.devicesCounted !== r.devicesLogged ? " (doesn't match the log)" : ""}</div></div>
          <div className="tile"><div className="tile__n">{r.changes.filter((c) => c.entity === "plan").length}</div><div className="tile__k">plan changes</div></div>
          <div className="tile"><div className="tile__n">{bugs.length}</div><div className="tile__k">planner bugs raised</div></div>
        </div>
        <div className="grid-2">
          <section className="card">
            <div className="card__head"><h2>Requests</h2><span className="small muted">{r.requests.filter((q) => q.status === "open").length} open</span></div>
            {r.requests.length ? r.requests.map((q) => (
              <div key={q.id} className="settings-row">
                <span className={`pill ${q.category === "planner_bug" ? "pill--r" : "pill--muted"}`}>{CATEGORY[q.category]}</span>
                <span className="settings-row__k">{q.body}<small>{q.raisedBy} · {q.day ? `${shortDay(q.day)} ${q.half ?? ""}` : stamp(q.createdAt)}{q.decision ? ` · ${q.decidedBy}: ${q.decision}` : ""}{q.jiraKey ? ` · ${q.jiraKey}` : ""}</small></span>
                <span className={`pill ${q.status === "open" ? "pill--a" : "pill--g"}`}>{q.status}</span>
              </div>
            )) : <p className="small muted">Nothing raised this week.</p>}
          </section>
          <section className="card">
            <div className="card__head"><h2>Issues reported</h2></div>
            {r.issues.length ? r.issues.map((s) => (
              <div key={s.id} className="settings-row">
                <span className="settings-row__k"><b>{s.person}</b> {shortDay(s.day)} {s.half} · {s.task ?? "other work"}, {s.actualQty ?? 0}{s.plannedQty ? ` of ${s.plannedQty}` : ""}<small>{s.issue ?? s.note}</small></span>
              </div>
            )) : <p className="small muted">No issues reported.</p>}
          </section>
          <section className="card">
            <div className="card__head"><h2>How it went</h2><span className="small muted">From the end-of-task survey</span></div>
            {r.ratings.length ? (
              <table className="table"><thead><tr><th>Task</th><th className="num">Smoothly (1–3)</th><th className="num">Enjoyed (1–5)</th><th className="num">Answers</th></tr></thead>
                <tbody>{r.ratings.map((x) => <tr key={x.task}><td><TaskChip task={x.task} name={byCode[x.task]?.name} group={byCode[x.task]?.group} /></td><td className="num">{x.smoothness ?? "–"}</td><td className="num">{x.enjoyment ?? "–"}</td><td className="num">{x.n}</td></tr>)}</tbody></table>
            ) : <p className="small muted">No survey answers yet.</p>}
          </section>
          <section className="card">
            <div className="card__head"><h2>Other work</h2></div>
            {r.otherWork.length ? r.otherWork.map((s) => (
              <div key={s.id} className="settings-row"><span className="settings-row__k"><b>{s.person}</b> {shortDay(s.day)} {s.half}<small>{s.note ?? s.task}</small></span></div>
            )) : <p className="small muted">None logged.</p>}
          </section>
        </div>
        <section className="card scroll-x">
          <div className="card__head"><h2>What changed, and why</h2><span className="small muted">Read by the monthly review</span></div>
          <table className="table">
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>Why</th></tr></thead>
            <tbody>{r.changes.slice(0, 60).map((c) => (
              <tr key={c.id}><td className="muted" style={{ whiteSpace: "nowrap" }}>{stamp(c.at)}</td><td>{c.by ?? c.source}</td><td>{c.summary}</td><td className="muted">{c.reason ?? ""}</td></tr>
            ))}</tbody>
          </table>
        </section>
      </div>
    </Page>
  );
}
